<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { SOCIAL_SHARE_IMAGE, usePageHead } from '@composables/use-page-head'
import { usePlaybackStore } from '@state/stores/playback'
import { useRoute, useRouter } from 'vue-router'
import DataLayer from '@services/data-layer/index'
import Layout from '@layouts/main-layout'
import MixMetaTile from '@components/mix-meta-tile'
import TracklistBlock from '@components/tracklist-block'
import MixGridBlock from '@components/mix-grid-block'

const route = useRoute()
const router = useRouter()
const playbackStore = usePlaybackStore()

const loadedMixId = ref(0)
// The placeholder shape the tile renders against until the fetch lands.
const mix = ref({
    id: 0,
    type: 'mix',
    genre: [],
    artist: { id: 0, type: 'artist' },
    'track_list': Array.from(Array(7), () => ({})),
})
const relatedMixes = ref([])

const playback = computed(() => playbackStore)

const status = computed(() => playback.value?.status)

function handleUrlPlayParam() {
    if (
        status.value === 'playing' &&
        playback.value?.resource?.id === loadedMixId.value &&
        playback.value?.resource?.type === 'mix'
    ) {
        router.replace({ query: { ...route.query, play: 'true' } })
    } else {
        const { play, ...rest } = route.query
        if (JSON.stringify(rest) === JSON.stringify(route.query)) return
        router.replace({ query: rest })
    }
}

watch(status, handleUrlPlayParam)

onMounted(() => {
    Promise.all([
        DataLayer.mixes.getById(route.params.id),
        DataLayer.mixes.getRelatedByMix(route.params.id),
    ]).then(([mixModel, related]) => {
        mixModel.body.type = 'mix'
        loadedMixId.value = mixModel.body.id
        mix.value = mixModel.body
        relatedMixes.value = related.body
        handleUrlPlayParam()
    })
})

usePageHead(computed(() => {
    const hasData = mix.value.show && mix.value.artist
    const genre = mix.value.genre?.length ? mix.value.genre[0] : ''

    return {
        title: hasData ? `${mix.value.artist.title} - ${mix.value.show.title}` : 'Mix',
        description: hasData
            ? `A mix by ${mix.value.artist.title} on ${mix.value.show.title}${genre ? ' • ' + genre : ''}`
            : 'Listen on FRISKY',
        image: mix.value.album_art?.url || mix.value.show?.album_art?.url || SOCIAL_SHARE_IMAGE,
        type: 'music.song',
    }
}))
</script>

<template>
    <Layout>
        <BaseContent>
            <div class="container">
                <BaseSection padded>
                    <h6>Mix</h6>
                    <div class="content">
                        <div class="details">
                            <MixMetaTile :mix="mix" />
                        </div>
                        <div>
                            <h6 class="subtitle">Tracklist</h6>
                            <TracklistBlock :tracklist="mix['track_list']" />
                        </div>
                    </div>
                </BaseSection>
            </div>
            <BaseSection
                v-if="relatedMixes && relatedMixes.length"
                padded
                title="Related Mixes"
            >
                <MixGridBlock :mixes="relatedMixes" />
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    margin-top: $s-h;
}

.content {
    @include widescreen {
        display: grid;
        grid-template-columns: 2fr 5fr;
        gap: $s-xxxl;
    }
}

.details {
    max-width: 300px;
    margin: 0 auto;

    @include widescreen {
        margin: unset;
    }
}

.subtitle {
    @extend %contentSubtitleS;

    min-width: 100%;
    margin: 0;
}
</style>
